/**
 * 阶段状态徽章组件
 * 可访问性增强：图标 + 文字 + 颜色三重指示
 */

import { CheckCircle, Play, Clock, AlertCircle, Circle, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

import { STAGE_STATUS_COLORS, TIME_STATUS_LABELS, type TimeStatus } from '../workflow-constants'

// ==================== 类型定义 ====================

export type StageStatus = 'completed' | 'current' | 'pending'

interface StageStatusBadgeProps {
  status: StageStatus
  timeStatus?: TimeStatus | null
  compact?: boolean
  loading?: boolean
  showLabel?: boolean
  className?: string
}

// ==================== 状态配置 ====================

const STATUS_CONFIG = {
  completed: {
    icon: CheckCircle,
    label: '已完成',
    colors: STAGE_STATUS_COLORS.completed,
  },
  current: {
    icon: Play,
    label: '进行中',
    colors: STAGE_STATUS_COLORS.current,
  },
  pending: {
    icon: Circle,
    label: '待处理',
    colors: STAGE_STATUS_COLORS.pending,
  },
} as const

const TIME_STATUS_CONFIG = {
  approaching_deadline: {
    icon: Clock,
    colors: STAGE_STATUS_COLORS.approaching,
  },
  overdue: {
    icon: AlertCircle,
    colors: STAGE_STATUS_COLORS.overdue,
  },
  on_track: {
    icon: Play,
    colors: STAGE_STATUS_COLORS.current,
  },
} as const

// ==================== 组件实现 ====================

export function StageStatusBadge({
  status,
  timeStatus,
  compact = false,
  loading = false,
  showLabel = true,
  className,
}: StageStatusBadgeProps) {
  const config = STATUS_CONFIG[status]

  // 当前阶段且有时间状态时，使用时间状态的配置
  const effectiveColors =
    status === 'current' && timeStatus ? TIME_STATUS_CONFIG[timeStatus].colors : config.colors

  const Icon =
    status === 'current' && timeStatus ? TIME_STATUS_CONFIG[timeStatus].icon : config.icon

  const label =
    status === 'current' && timeStatus
      ? `${config.label} (${TIME_STATUS_LABELS[timeStatus]})`
      : config.label

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        compact ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-xs',
        effectiveColors.bg,
        effectiveColors.text,
        className
      )}
      role="status"
      aria-label={label}
    >
      {loading ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      ) : (
        <Icon className="h-3 w-3" aria-hidden="true" />
      )}
      {showLabel && <span>{config.label}</span>}
      {showLabel && timeStatus && status === 'current' && (
        <span className="text-[10px] opacity-75">({TIME_STATUS_LABELS[timeStatus]})</span>
      )}
    </span>
  )
}

/**
 * 阶段状态图标
 * 仅显示图标，用于进度条节点
 */
interface StageStatusIconProps {
  status: StageStatus
  timeStatus?: TimeStatus | null
  index?: number
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export function StageStatusIcon({
  status,
  timeStatus,
  index,
  size = 'md',
  className,
}: StageStatusIconProps) {
  const config = STATUS_CONFIG[status]

  const effectiveColors =
    status === 'current' && timeStatus ? TIME_STATUS_CONFIG[timeStatus].colors : config.colors

  const Icon =
    status === 'current' && timeStatus ? TIME_STATUS_CONFIG[timeStatus].icon : config.icon

  const sizeClasses = {
    sm: 'h-5 w-5 text-[10px]',
    md: 'h-6 w-6 text-xs',
    lg: 'h-8 w-8 text-sm',
  }

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4',
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full',
        sizeClasses[size],
        effectiveColors.bg,
        effectiveColors.text,
        className
      )}
      role="img"
      aria-label={config.label}
    >
      {status === 'pending' && typeof index === 'number' ? (
        <span>{index + 1}</span>
      ) : (
        <Icon className={iconSizeClasses[size]} />
      )}
    </div>
  )
}
